import React from 'react';
import type { LucideIcon } from 'lucide-react';

type BaseProps = {
  className?: string;
  children: React.ReactNode;
};

export function PremiumPageShell({ className = '', children }: BaseProps) {
  return <div className={`op-shell min-h-0 ${className}`}>{children}</div>;
}

type SectionCardProps = {
  title?: string;
  subtitle?: string;
  actions?: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
};

export function PremiumSectionCard({ className = '', title, subtitle, actions, children }: SectionCardProps) {
  return (
    <section className={`op-card p-5 ${className}`}>
      {(title || subtitle || actions) && (
        <div className="mb-4 flex items-start justify-between gap-4">
          <div className="min-w-0">
            {title && <div className="op-section-label">{title}</div>}
            {subtitle && <p className="mt-2 text-sm leading-relaxed op-muted">{subtitle}</p>}
          </div>
          {actions && <div className="shrink-0">{actions}</div>}
        </div>
      )}
      {children}
    </section>
  );
}

type ClinicalCardProps = {
  title: string;
  eyebrow?: string;
  description?: string;
  items?: string[];
  variant?: 'default' | 'warning' | 'success' | 'neutral';
  className?: string;
};

export function PremiumClinicalCard({
  title,
  eyebrow,
  description,
  items = [],
  variant = 'default',
  className = '',
}: ClinicalCardProps) {
  const accentClass =
    variant === 'warning' ? 'text-amber-300' : variant === 'success' ? 'text-emerald-300' : variant === 'neutral' ? 'text-slate-300' : 'text-cyan-300';
  const bulletClass =
    variant === 'warning' ? 'bg-amber-300' : variant === 'success' ? 'bg-emerald-300' : variant === 'neutral' ? 'bg-slate-300' : 'bg-cyan-300';

  return (
    <article className={`op-card p-4 text-left ${className}`}>
      {eyebrow && <div className={`text-[10px] font-bold uppercase tracking-[0.18em] ${accentClass}`}>{eyebrow}</div>}
      <h4 className="mt-1 text-[13px] font-semibold uppercase tracking-[0.18em] op-title">{title}</h4>
      {description && <p className="mt-2 text-sm leading-6 op-muted">{description}</p>}
      {items.length > 0 && (
        <ul className="mt-3 space-y-2">
          {items.map((item, index) => (
            <li key={`${title}-${index}`} className="flex gap-2 text-sm leading-6 op-title">
              <span className={`mt-2 h-1.5 w-1.5 shrink-0 rounded-full ${bulletClass}`} />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      )}
    </article>
  );
}

type EmptyStateProps = {
  title: string;
  subtitle: string;
  icon: LucideIcon;
  action?: React.ReactNode;
  className?: string;
};

export function PremiumEmptyState({ title, subtitle, icon: Icon, action, className = '' }: EmptyStateProps) {
  return (
    <div className={`op-empty-state op-card p-8 text-center ${className}`}>
      <div className="flex h-20 w-20 items-center justify-center rounded-full border border-cyan-300/15 bg-cyan-500/10 shadow-[0_0_20px_rgba(34,211,238,0.08)]">
        <Icon className="h-10 w-10 text-cyan-300" />
      </div>
      <div className="space-y-2">
        <h3 className="text-[18px] font-bold text-[color:var(--op-text)]">{title}</h3>
        <p className="mx-auto max-w-[420px] text-sm leading-6 text-[color:var(--op-text-muted)]">{subtitle}</p>
      </div>
      {action}
    </div>
  );
}

type ActionButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'secondary' | 'danger';
  icon?: LucideIcon;
  iconClassName?: string;
};

export function PremiumActionButton({ variant = 'secondary', className = '', icon: Icon, iconClassName = '', children, ...props }: ActionButtonProps) {
  const variantClass =
    variant === 'primary' ? 'op-btn-primary' : variant === 'danger' ? 'op-btn-danger' : 'op-btn-secondary';
  return (
    <button
      {...props}
      className={`inline-flex items-center justify-center gap-2 rounded-[16px] px-4 py-2.5 text-sm font-bold transition-all duration-200 hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-50 ${variantClass} ${className}`}
    >
      {Icon && <Icon className={`h-4 w-4 ${iconClassName}`} />}
      {children}
    </button>
  );
}

export function PremiumInput(props: React.InputHTMLAttributes<HTMLInputElement>) {
  return <input {...props} className={`op-input w-full px-4 py-3 text-sm placeholder:text-[color:var(--op-text-muted)] ${props.className || ''}`} />;
}

type ModuleCardProps = React.HTMLAttributes<HTMLDivElement> & {
  children: React.ReactNode;
};

export function PremiumModuleCard({ className = '', children, ...props }: ModuleCardProps) {
  return (
    <div
      {...props}
      className={`op-card p-5 transition-all duration-200 hover:-translate-y-0.5 ${className}`}
    >
      {children}
    </div>
  );
}

export function PremiumGrid({ className = '', children }: BaseProps) {
  return <div className={`grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3 ${className}`}>{children}</div>;
}

export { type ClinicalCardProps };
